import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { isHttpSuccess } from 'src/api/utils/httpStatus';
import IconButton from 'common/buttons/IconButton/IconButton';
import DialogV2 from 'common/modal/DialogV2';
import Notifier from 'common/helpers/Notifier';
import { BarLoader } from '@unite-us/app-components';
import { FACESHEET } from 'common/utils/EventTracker/utils/eventConstants';
import { hbh1509ClientsDrawer } from 'src/common/utils/FeatureFlags/flags';
import {
  useDeletePersonalRelationship,
  useFetchPersonalRelationships,
} from '../Common/api';
import RelationshipCard from '../Common/RelationshipCard';
import RelationshipDeleteContent from '../Common/RelationshipDeleteContent';
import RelationshipListItem from '../Common/RelationshipListItem';
import RelationshipsList from '../Common/RelationshipsList';
import useFamilyMemberForm from './forms/useFamilyMemberForm';
import './FamilyMember.scss';

const DISPLAY_NAME = 'family-members';

const FamilyMember = ({
 person, className, shouldPortalizeDialogs, hbh1509ClientsDrawerEnabled,
}) => {
  if (isEmpty(person)) {
    return null;
  }

  const {
    data: personalRelationships = [], isFetching, isError,
  } = useFetchPersonalRelationships({
    personId: person.id,
    familyMember: true,
    careTeamMember: false,
  });

  // Combine prop from parent with feature flag as fallback
  const finalShouldPortalize = shouldPortalizeDialogs ?? hbh1509ClientsDrawerEnabled;

  const dialogRef = useRef(null);
  React.useEffect(() => {
    if (isError) {
      Notifier.dispatch('Error fetching family members');
    }
  }, [isError]);
  const [currentPersonalRelationship, setCurrentPersonalRelationship] =
    useState(null);
  const [FamilyMemberFormDialog, openFamilyMemberForm] = useFamilyMemberForm({
    personId: person.id,
    shouldPortalizeDialogs: finalShouldPortalize,
  });

  const { deleteRecord: deletePersonalRelationship } =
    useDeletePersonalRelationship();
  const confirmDelete = async () => {
    dialogRef.current.closeDialog();
    const response = await deletePersonalRelationship(
      currentPersonalRelationship.id,
      {},
    );
    if (isHttpSuccess(response.status)) {
      Notifier.dispatch(response.status, 'Family member successfully deleted.');
      window.eventTracker(FACESHEET.familyMemberRemoved, {
        relation_to_contact: currentPersonalRelationship.relationship,
      });
    }
  };

  const deleteButtonOnClickHandler = (e, pr) => {
    e.stopPropagation();
    setCurrentPersonalRelationship(pr);
    dialogRef.current.openDialog();
  };
  const editButtonClickHandler = (e, pr) => {
    e.stopPropagation();
    window.eventTracker(FACESHEET.familyMemberEditClicked, {
      relation_to_contact: pr.relationship,
    });
    openFamilyMemberForm(pr, personalRelationships.length);
  };

  return (
    <RelationshipCard
      title="Family Members"
      addButtonLabel="Add Family Member"
      className={classNames(DISPLAY_NAME, className)}
      onAddClick={() => openFamilyMemberForm(null, personalRelationships.length)}
    >
      {isFetching ? (
        <BarLoader className="h-60" size="full" />
      ) : (
        <RelationshipsList name="family">
          {personalRelationships.map((pr) => (
            <RelationshipListItem
              key={pr.id}
              personalRelationshipId={pr.id}
              fullName={pr.related_person?.full_name}
              relationship={pr.relationship}
              onClick={() => openFamilyMemberForm(pr, personalRelationships.length)}
              linkedFamilyMemberId={
                pr.shouldDisplayLink ? pr.related_person.id : ''
              }
            >
              <IconButton
                className={classNames(`${DISPLAY_NAME}__delete-btn`, 'pr-2')}
                labelText="Delete"
                icon="IconTrash"
                onClick={(e) => deleteButtonOnClickHandler(e, pr)}
                removeMargin
              />
              <IconButton
                className={`${DISPLAY_NAME}__edit-btn`}
                labelText="Edit"
                icon="IconPencil"
                onClick={(e) => editButtonClickHandler(e, pr)}
                removeMargin
              />
            </RelationshipListItem>
          ))}
        </RelationshipsList>
      )}
      {FamilyMemberFormDialog}
      <DialogV2
        ref={dialogRef}
        title="Delete Family Member"
        confirmLabel="Delete"
        cancelHandler={dialogRef?.current?.closeDialog || (() => {})}
        confirmationHandler={confirmDelete}
      >
        <RelationshipDeleteContent memberType="Family" />
      </DialogV2>
    </RelationshipCard>
  );
};

FamilyMember.propTypes = {
  className: PropTypes.string,
  person: PropTypes.object.isRequired,
  shouldPortalizeDialogs: PropTypes.bool,
  hbh1509ClientsDrawerEnabled: PropTypes.bool,
};

FamilyMember.defaultProps = {
  className: '',
  shouldPortalizeDialogs: false,
};

function mapStateToProps(state) {
  return {
    hbh1509ClientsDrawerEnabled: hbh1509ClientsDrawer(state),
  };
}

export default connect(mapStateToProps)(FamilyMember);
